/**
 * Bid Mutation Hooks
 * React Query mutation hooks for bid write operations
 *
 * Following DDD Principles:
 * - Mutations call service layer directly
 * - Optimistic updates for instant UI feedback
 * - Cache invalidation triggers refetch
 * - NO business logic in hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceProviderService } from '@/services/service-provider.service';
import { bidKeys } from './useBidData';
import type { CreateBidRequest } from '@calwmp/shared';

/**
 * Hook to update an existing bid
 * Only allowed if status === 'pending'
 */
export function useUpdateBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bidId, updates }: { bidId: string; updates: Partial<CreateBidRequest> }) =>
      serviceProviderService.updateBid(bidId, updates),
    onSuccess: (_data, variables) => {
      // Invalidate specific bid detail
      queryClient.invalidateQueries({ queryKey: bidKeys.detail(variables.bidId) });
      // Invalidate bid lists to show updated data
      queryClient.invalidateQueries({ queryKey: bidKeys.lists() });
      // Invalidate dashboard metrics
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Hook to withdraw a bid
 * Only allowed if status === 'pending'
 */
export function useWithdrawBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bidId, reason }: { bidId: string; reason?: string }) =>
      serviceProviderService.withdrawBid(bidId, reason),
    onSuccess: (_data, variables) => {
      // Invalidate specific bid detail
      queryClient.invalidateQueries({ queryKey: bidKeys.detail(variables.bidId) });
      // Invalidate bid lists
      queryClient.invalidateQueries({ queryKey: bidKeys.lists() });
      // Invalidate dashboard metrics
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Hook to submit a new bid
 * Used when submitting bids from marketplace
 */
export function useSubmitBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, bid }: { planId: string; bid: CreateBidRequest }) =>
      serviceProviderService.submitBid(planId, bid),
    onSuccess: () => {
      // Invalidate all bid queries
      queryClient.invalidateQueries({ queryKey: bidKeys.all });
      // Invalidate dashboard metrics
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      // Invalidate available plans (one less plan available)
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}
